import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Palette, RotateCcw, Eye } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext.tsx';
import PreviewDashboard from './PreviewDashboard';
import { toast } from 'sonner';
import Colors from './custom/Colors.tsx';
import Basic from './custom/Basic.tsx';
import Logo from './custom/Logo.tsx';
import Message from './custom/Message.tsx';

function BrandingCustomizer() {
  const { branding, isWhiteLabel, resetBranding } = useBranding();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');

  const [colors, setColors] = useState(branding.colors);
  const [companyName, setCompanyName] = useState(branding.companyName);
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl || '');
  const [faviconUrl, setFaviconUrl] = useState(branding.faviconUrl || '');
  const [customCopy, setCustomCopy] = useState(branding.customCopy);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const hashToTabMap: Record<string, string> = { 'copy': 'copy','colors': 'colors' };
        setActiveTab(hashToTabMap[hash]);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleReset = async () => {
    if (!isWhiteLabel) return toast.error('Only white-label partners can customize branding');
    await resetBranding();
  };


  if (!isWhiteLabel) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Palette className="h-5 w-5 mr-2" />
              Brand Customization
            </CardTitle>
            <CardDescription>
              Brand customization is only available for white-label partners
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                White-Label Feature
              </h3>
              <p className="text-gray-600 mb-4">
                Upgrade to a white-label partnership to customize your branding, colors, and messaging.
              </p>
              <Button variant="outline">
                Contact Sales
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Palette className="h-5 w-5 mr-2" />
                Brand Customization
              </CardTitle>
              <CardDescription>
                Customize the appearance of the platform for your customers
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
               <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
                 <DialogTrigger asChild>
                   <Button variant="outline" size="sm">
                     <Eye className="h-4 w-4 mr-2" />
                     Live Preview
                   </Button>
                 </DialogTrigger>
                 <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 overflow-hidden">
                   <div className="w-full h-full">
                     <PreviewDashboard
                        branding={{
                          ...branding,  
                          colors,
                          companyName,
                          logoUrl,
                          faviconUrl,
                          customCopy,
                        }}
                      />
                   </div>
                 </DialogContent>
               </Dialog>
               <Button variant="outline" size="sm" onClick={handleReset}>
                 <RotateCcw className="h-4 w-4 mr-2" />
                 Reset
               </Button>
             </div>
          </div>
        </CardHeader>
        <CardContent>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="colors">Colors</TabsTrigger>
              <TabsTrigger value="identity">Logo & Identity</TabsTrigger>
              <TabsTrigger value="copy">Basic Copy</TabsTrigger>
              <TabsTrigger value="messages">Brand Messages</TabsTrigger>
            </TabsList>

            <TabsContent value="colors" className="space-y-6">
              <Colors colors={colors} setColors={setColors} />
            </TabsContent>

            <TabsContent value="identity" className="space-y-6">
              <Logo logoUrl={logoUrl} setLogoUrl={setLogoUrl} faviconUrl={faviconUrl} setFaviconUrl={setFaviconUrl} companyName={companyName} setCompanyName={setCompanyName} />
            </TabsContent>  

            <TabsContent value="copy" className="space-y-6">
              <Basic customCopy={customCopy} setCustomCopy={setCustomCopy} />
            </TabsContent>

            <TabsContent value="messages" className="space-y-6">
              <Message customCopy={customCopy} setCustomCopy={setCustomCopy} />
            </TabsContent>
          </Tabs>


        </CardContent>
      </Card>
    </div>
  );
}

export default BrandingCustomizer;